import { useEffect, useState } from "react";
import { Bell, Clock, Warning as AlertTriangle, CheckCircle, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useUserAccess } from "@/hooks/useUserAccess";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Notification {
  id: string;
  type: 'trial_expiry' | 'subscription_expiry' | 'listing_expiry' | 'payment_failed' | 'general';
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  read: boolean;
  created_at: string;
  expires_at?: string;
  action_url?: string;
  action_text?: string;
}

export const NotificationCenter = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const { accessLevel, subscriptionEnd, isLoading } = useUserAccess();
  const { toast } = useToast();

  // Generează notificări bazate pe statusul utilizatorului
  useEffect(() => {
    if (isLoading) return;

    const generatedNotifications: Notification[] = [];

    if (accessLevel === 'subscribed' && subscriptionEnd) {
      const endDate = new Date(subscriptionEnd);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      // Notificare pentru trial care expiră în 7 zile sau mai puțin
      if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
        const isTrial = daysUntilExpiry <= 30; // Presupunem că trial-ul este ~30 zile
        
        generatedNotifications.push({
          id: 'expiry_warning',
          type: isTrial ? 'trial_expiry' : 'subscription_expiry',
          title: isTrial ? 'Trial-ul expiră curând' : 'Abonamentul expiră curând',
          message: `${isTrial ? 'Perioada ta gratuită' : 'Abonamentul'} expiră în ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'zi' : 'zile'}. ${isTrial ? 'Toate anunțurile vor fi dezactivate dacă nu te abonezi.' : 'Renewing your subscription will maintain access.'}`,
          severity: daysUntilExpiry <= 3 ? 'error' : 'warning',
          read: false,
          created_at: new Date().toISOString(),
          expires_at: endDate.toISOString(),
          action_url: '/pricing',
          action_text: isTrial ? 'Alege un plan' : 'Reînnoiește abonamentul'
        });
      }

      // Notificare pentru expirare în ziua curentă
      if (daysUntilExpiry === 0) {
        const isTrial = true; // În ziua expirării
        generatedNotifications.push({
          id: 'expiry_today',
          type: 'trial_expiry',
          title: '🚨 Trial-ul expiră astăzi!',
          message: 'Perioada ta gratuită expiră astăzi. Toate anunțurile vor fi dezactivate dacă nu te abonezi până la sfârșitul zilei.',
          severity: 'error',
          read: false,
          created_at: new Date().toISOString(),
          expires_at: endDate.toISOString(),
          action_url: '/pricing',
          action_text: 'Abonează-te ACUM'
        });
      }
    }

    if (accessLevel === 'logged_unsubscribed') {
      generatedNotifications.push({
        id: 'no_subscription',
        type: 'general',
        title: 'Activează-ți abonamentul',
        message: 'Pentru a lista anunțuri și accesa toate funcționalitățile, ai nevoie de un abonament activ.',
        severity: 'info',
        read: false,
        created_at: new Date().toISOString(),
        action_url: '/pricing',
        action_text: 'Vezi planurile'
      });
    }

    setNotifications(generatedNotifications);
  }, [accessLevel, subscriptionEnd, isLoading]);

  const markAsRead = (notificationId: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const dismissNotification = (notificationId: string) => {
    setNotifications(prev => 
      prev.filter(notification => notification.id !== notificationId)
    );
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getSeverityColor = (severity: Notification['severity']) => {
    switch (severity) {
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getSeverityIcon = (severity: Notification['severity']) => {
    switch (severity) {
      case 'error': return <AlertTriangle className="h-4 w-4" />;
      case 'warning': return <Clock className="h-4 w-4" />;
      case 'success': return <CheckCircle className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="relative">
      {/* Notification Bell */}
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setShowNotifications(!showNotifications)}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <Badge 
            variant="destructive" 
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
          >
            {unreadCount}
          </Badge>
        )}
      </Button>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-96 overflow-y-auto bg-white border rounded-lg shadow-lg z-50">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Notificări</h3>
            <p className="text-sm text-muted-foreground">
              {unreadCount > 0 ? `${unreadCount} notificări necitite` : 'Toate notificările sunt citite'}
            </p>
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 border-b last:border-b-0 ${notification.read ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-1 rounded-sm ${getSeverityColor(notification.severity)}`}>
                      {getSeverityIcon(notification.severity)}
                    </div>
                    <div className="flex-1 space-y-1">
                      <h4 className="text-sm font-medium">{notification.title}</h4>
                      <p className="text-xs text-muted-foreground">{notification.message}</p>
                      {notification.action_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => {
                            window.location.href = notification.action_url!;
                            markAsRead(notification.id);
                          }}
                        >
                          {notification.action_text}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!notification.read && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markAsRead(notification.id)}
                        className="h-6 w-6 p-0"
                      >
                        <CheckCircle className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismissNotification(notification.id)}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};